import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  api,
  DashboardStats,
  Roadmap,
  Module,
  Lesson,
  PersonalizedRoadmap,
  ModuleAssignment,
} from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Flame,
  Target,
  BookOpen,
  TrendingUp,
  Users,
  GraduationCap,
  Calendar,
  CheckCircle,
  Clock,
  AlertCircle,
  PlayCircle,
  ArrowRight,
  Trophy,
  Sparkles,
  X,
  Building2,
} from "lucide-react";
import LinkRequestNotifications from "../components/LinkRequestNotifications";
import ClassroomInvitations from "../components/ClassroomInvitations";
import {
  GamificationWidget,
  AchievementsShowcase,
} from "../components/Gamification";
import { ChaosMeter } from "../components/ChaosMeter";
import { RankProgression } from "../components/RankProgression";
import { WorldMap } from "../components/WorldMap";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSchoolStudent = user?.role === "student" && !!user?.school_id;
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [assignedModules, setAssignedModules] = useState<ModuleAssignment[]>([]);
  const [classrooms, setClassrooms] = useState<any[]>([]);
  const [assignments, setAssignments] = useState<any>({
    assignments: [],
    categorized: {},
  });
  const [nextLesson, setNextLesson] = useState<{
    roadmap: Roadmap;
    module: Module;
    lesson: Lesson;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [personalizedRoadmap, setPersonalizedRoadmap] =
    useState<PersonalizedRoadmap | null>(null);
  const [onboardingDismissed, setOnboardingDismissed] = useState(() => {
    return sessionStorage.getItem("onboarding-banner-dismissed") === "true";
  });
  const [sealedTechniques, setSealedTechniques] = useState(0);
  const [totalTechniques, setTotalTechniques] = useState(0);
  const [userXp, setUserXp] = useState<any>(null);

  const handleStartAssignment = (assignment: any) => {
    // Navigate to assignment practice page
    navigate(`/assignment/${assignment.id}`);
  };

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const [
          statsData,
          classroomsData,
          assignmentsData,
          roadmapsData,
          personalizedRoadmapData,
          techniquesData,
          progressData,
          xpData,
        ] = await Promise.all([
          api.getDashboard(),
          api.getStudentClassrooms().catch(() => ({ classrooms: [] })),
          api
            .getStudentAssignments()
            .catch(() => ({ assignments: [], categorized: {} })),
          api.getRoadmaps(true).catch(() => []),
          api.getPersonalizedRoadmap().catch(() => null),
          api.getTechniques({ view: "list" }).catch(() => []),
          api.getUserProgress().catch(() => []),
          api.getUserXP().catch(() => null),
        ]);
        setStats(statsData);
        setClassrooms(classroomsData.classrooms);
        setAssignments(assignmentsData);
        setPersonalizedRoadmap(personalizedRoadmapData);

        // Calculate sealed techniques (advanced or expert mastery)
        const sealed = progressData.filter((p: any) =>
          ["advanced", "expert"].includes(p.mastery_level)
        ).length;
        setSealedTechniques(sealed);
        setTotalTechniques(techniquesData.length);
        setUserXp(xpData);

        // Find the next lesson to work on
        let found = null;
        // First look for in-progress lessons
        for (const roadmap of roadmapsData) {
          if (!roadmap.modules) continue;
          for (const module of roadmap.modules) {
            if (!module.lessons) continue;
            for (const lesson of module.lessons) {
              if (lesson.progress?.status === "in_progress") {
                found = { roadmap, module, lesson };
                break;
              }
            }
            if (found) break;
          }
          if (found) break;
        }

        // If no in-progress lesson, look for the first unlocked one
        if (!found) {
          for (const roadmap of roadmapsData) {
            if (!roadmap.modules) continue;
            for (const module of roadmap.modules) {
              if (!module.lessons) continue;
              for (const lesson of module.lessons) {
                if (
                  lesson.progress?.status === "unlocked" &&
                  !lesson.progress?.completed_at
                ) {
                  found = { roadmap, module, lesson };
                  break;
                }
              }
              if (found) break;
            }
            if (found) break;
          }
        }

        // If still nothing, and they have roadmaps, maybe start the first one
        if (
          !found &&
          roadmapsData.length > 0 &&
          roadmapsData[0].modules?.[0]?.lessons?.[0]
        ) {
          const firstLesson = roadmapsData[0].modules[0].lessons[0];
          if (firstLesson.progress?.status !== "completed") {
            found = {
              roadmap: roadmapsData[0],
              module: roadmapsData[0].modules[0],
              lesson: firstLesson,
            };
          }
        }

        setNextLesson(found);

        // Load assigned modules for school students
        if (user?.school_id) {
          const modulesData = await api.getAssignedModules().catch(() => ({ modules: [] }));
          setAssignedModules(modulesData.modules || []);
        }
      } catch (error) {
        console.error("Failed to load dashboard:", error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, []);

  // Helper functions for school student module display
  const formatModuleName = (moduleId: string): string => {
    const parts = moduleId.split(".");
    const name = parts[0]
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
    const moduleNum = parts[1]?.replace("module_", "Module ") || "";
    return moduleNum ? `${name} - ${moduleNum}` : name;
  };

  const formatRoadmapName = (roadmapId: string): string => {
    return roadmapId
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const getStatusLabel = (
    status: string,
    mod: ModuleAssignment,
  ): string => {
    if (status === "completed") return "Completed";
    if (status === "missed") return "Missed";
    if (status === "not_started") return "Not Started";
    // in_progress: compute on_track vs at_risk
    if (
      mod.progress.days_remaining !== null &&
      mod.progress.days_remaining < mod.window_days * 0.5 &&
      mod.progress.practice_days_completed < mod.practice_days_required * 0.5
    ) {
      return "At Risk";
    }
    return "On Track";
  };

  const getStatusVariant = (
    status: string,
  ): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case "completed":
        return "default";
      case "missed":
        return "destructive";
      case "not_started":
        return "secondary";
      default:
        return "outline";
    }
  };

  if (loading) {
    return <div className="container mx-auto px-4 py-8">Loading...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Parent Link Request Notifications (only shown to student users with pending requests) */}
      <div className="mb-6">
        <LinkRequestNotifications />
      </div>

      {/* Classroom Invitations (only shown to students with pending invitations) */}
      <div className="mb-6">
        <ClassroomInvitations />
      </div>

      {/* Onboarding Prompt - shown to users who don't have a personalized roadmap (hidden for school students) */}
      {!isSchoolStudent && !onboardingDismissed && !personalizedRoadmap && (
        <div className="mb-6">
          <Card className="border-2 border-indigo-300 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950 dark:to-purple-950 dark:border-indigo-700 relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute -right-8 -top-8 opacity-10">
              <Sparkles className="w-32 h-32 text-indigo-600" />
            </div>

            {/* Dismiss button */}
            <button
              onClick={() => {
                setOnboardingDismissed(true);
                sessionStorage.setItem("onboarding-banner-dismissed", "true");
              }}
              className="absolute top-3 right-3 p-1 rounded-full hover:bg-indigo-100 dark:hover:bg-indigo-900 transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4 text-indigo-400" />
            </button>

            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-indigo-900 dark:text-indigo-100">
                <Sparkles className="w-5 h-5 text-indigo-500" />
                Get Your Own Quest Map! 🗺️
              </CardTitle>
              <CardDescription className="text-indigo-700 dark:text-indigo-300">
                Answer a few quick questions (about 3 minutes) and we'll build
                a training path made just for you
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-2">
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <div className="flex-1">
                  <ul className="text-sm text-indigo-800 dark:text-indigo-200 space-y-1">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Discover your math superpowers
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Get your own week-by-week quest map
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Watch your progress grow with every step
                    </li>
                  </ul>
                </div>
                <Button
                  size="lg"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2"
                  onClick={() => navigate("/onboarding/goals")}
                >
                  Let's Go!
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* School Student Banner */}
      {isSchoolStudent && (
        <div className="mb-6">
          <Card className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <Building2 className="w-8 h-8" />
                <div>
                  <h2 className="text-xl font-bold">Welcome back!</h2>
                  <p className="text-blue-100">
                    {classrooms.length > 0
                      ? `${classrooms[0]?.classroom?.name || classrooms[0]?.name || "Your Classroom"}`
                      : "Loading your classroom..."}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* School Student: Practice Streak */}
      {isSchoolStudent && assignedModules.length > 0 && (
        <div className="mb-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Flame className="w-5 h-5 text-orange-500" />
                Practice Streak
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center">
                <div className="text-3xl font-bold">
                  {assignedModules.reduce(
                    (sum, m) => sum + m.progress.practice_days_completed,
                    0,
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  Total practice days this week
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* School Student: Your Modules */}
      {isSchoolStudent && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Your Modules
          </h2>
          {assignedModules.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center text-gray-500">
                Your teacher hasn't assigned any modules yet.
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {assignedModules.map((mod) => (
                <Card
                  key={mod.assignment_id}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() =>
                    navigate(`/arena/${mod.roadmap_id.replace(/_/g, "-")}`)
                  }
                >
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-medium">
                        {formatModuleName(mod.module_id)}
                      </h3>
                      <Badge variant={getStatusVariant(mod.progress.status)}>
                        {getStatusLabel(mod.progress.status, mod)}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500 mb-2">
                      {formatRoadmapName(mod.roadmap_id)}
                    </p>
                    <div className="flex items-center gap-2 text-sm">
                      <span>
                        Practice: {mod.progress.practice_days_completed}/
                        {mod.practice_days_required} days
                      </span>
                      {mod.progress.days_remaining !== null && (
                        <span className="text-gray-400">
                          | {mod.progress.days_remaining} days left
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* School Student: Upcoming Deadlines */}
      {isSchoolStudent &&
        assignedModules.filter(
          (m) =>
            m.progress.status === "in_progress" &&
            m.progress.days_remaining !== null,
        ).length > 0 && (
          <div className="mb-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-yellow-500" />
                  Upcoming Deadlines
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {assignedModules
                    .filter(
                      (m) =>
                        m.progress.status === "in_progress" &&
                        m.progress.days_remaining !== null,
                    )
                    .sort(
                      (a, b) =>
                        (a.progress.days_remaining ?? 99) -
                        (b.progress.days_remaining ?? 99),
                    )
                    .slice(0, 3)
                    .map((mod) => (
                      <div
                        key={mod.assignment_id}
                        className="flex justify-between items-center"
                      >
                        <span className="text-sm">
                          {formatModuleName(mod.module_id)}
                        </span>
                        <Badge
                          variant={
                            mod.progress.days_remaining! <= 2
                              ? "destructive"
                              : "secondary"
                          }
                        >
                          {mod.progress.days_remaining} days left
                        </Badge>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

      {/* My Classrooms */}
      {classrooms.length > 0 && (
        <div className="mb-6">
          <Card className="border-2 border-blue-200 bg-blue-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-900">
                <GraduationCap className="w-5 h-5" />
                My Classrooms ({classrooms.length})
              </CardTitle>
              <CardDescription className="text-blue-700">
                Classes you're enrolled in
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {classrooms.map((enrollment: any) => (
                  <div
                    key={enrollment.id}
                    className="bg-white p-4 rounded-lg border border-blue-200 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg text-gray-900">
                          {enrollment.classroom?.name || "Unknown Classroom"}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {enrollment.classroom?.grade_level &&
                            `${enrollment.classroom.grade_level} • `}
                          {enrollment.classroom?.subject || "Mathematics"}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Teacher:{" "}
                          {enrollment.classroom?.teacher_info?.username ||
                            "Unknown"}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Users className="w-4 h-4" />
                        <span>{enrollment.classroom?.student_count || 0}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Assignments */}
      {(assignments.categorized?.upcoming?.length > 0 ||
        assignments.categorized?.in_progress?.length > 0 ||
        assignments.categorized?.submitted?.length > 0 ||
        assignments.categorized?.overdue?.length > 0) && (
        <div className="mb-6">
          <Card className="border-2 border-purple-200 bg-purple-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-purple-900">
                <Calendar className="w-5 h-5" />
                My Assignments
              </CardTitle>
              <CardDescription className="text-purple-700">
                Assignments from your teachers
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Overdue Assignments */}
              {assignments.categorized?.overdue?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Overdue ({assignments.categorized.overdue.length})
                  </h4>
                  <div className="space-y-2">
                    {assignments.categorized.overdue
                      .slice(0, 3)
                      .map((assignment: any) => (
                        <div
                          key={assignment.id}
                          className="bg-red-50 border border-red-200 p-3 rounded-lg"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">
                                {assignment.title}
                              </p>
                              <p className="text-sm text-gray-600">
                                {assignment.classrooms?.name}
                              </p>
                              <p className="text-xs text-red-600 mt-1">
                                Due:{" "}
                                {new Date(
                                  assignment.due_date,
                                ).toLocaleDateString()}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleStartAssignment(assignment)}
                            >
                              Start
                            </Button>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Upcoming Assignments */}
              {assignments.categorized?.upcoming?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Upcoming ({assignments.categorized.upcoming.length})
                  </h4>
                  <div className="space-y-2">
                    {assignments.categorized.upcoming
                      .slice(0, 3)
                      .map((assignment: any) => (
                        <div
                          key={assignment.id}
                          className="bg-white border border-blue-200 p-3 rounded-lg"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">
                                {assignment.title}
                              </p>
                              <p className="text-sm text-gray-600">
                                {assignment.classrooms?.name}
                              </p>
                              {assignment.due_date && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Due:{" "}
                                  {new Date(
                                    assignment.due_date,
                                  ).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                            <Button
                              size="sm"
                              onClick={() => handleStartAssignment(assignment)}
                            >
                              Start
                            </Button>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Submitted/Graded */}
              {(assignments.categorized?.submitted?.length > 0 ||
                assignments.categorized?.graded?.length > 0) && (
                <div>
                  <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Completed (
                    {(assignments.categorized.submitted?.length || 0) +
                      (assignments.categorized.graded?.length || 0)}
                    )
                  </h4>
                  <div className="space-y-2">
                    {[
                      ...(assignments.categorized.submitted || []),
                      ...(assignments.categorized.graded || []),
                    ]
                      .slice(0, 2)
                      .map((assignment: any) => (
                        <div
                          key={assignment.id}
                          className="bg-green-50 border border-green-200 p-3 rounded-lg"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">
                                {assignment.title}
                              </p>
                              <p className="text-sm text-gray-600">
                                {assignment.classrooms?.name}
                              </p>
                              <p className="text-xs text-green-600 mt-1">
                                {assignment.my_submission?.[0]?.status ===
                                "graded"
                                  ? "Graded"
                                  : "Submitted"}
                                {assignment.my_submission?.[0]?.score &&
                                  ` • ${assignment.my_submission[0].score}%`}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {!isSchoolStudent && (
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Warrior's Sanctum
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Ready to train? Pick up where you left off
            </p>
          </div>
        </div>
      )}

      {/* Chaos Meter - World Status */}
      {!isSchoolStudent && (
        <div className="mb-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChaosMeter
            sealed={sealedTechniques}
            total={totalTechniques}
            globalRestored={totalTechniques > 0 ? (sealedTechniques / totalTechniques) * 100 : 0}
          />
          <WorldMap />
        </div>
      )}

      {/* Continue Learning Section */}
      {!isSchoolStudent && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Continue Training
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Hero Card - Next Lesson */}
            <div className="lg:col-span-2">
              {nextLesson ? (
                <Card
                  className="h-full border-2 border-blue-100 dark:border-blue-900 bg-gradient-to-br from-blue-50 to-white dark:from-blue-950 dark:to-gray-900 overflow-hidden relative group hover:border-blue-300 transition-colors cursor-pointer"
                  onClick={() => navigate(`/lesson/${nextLesson.lesson.id}`)}
                >
                  {/* Background Pattern/Icon */}
                  <div className="absolute -right-8 -bottom-8 opacity-10 transform rotate-12 group-hover:scale-110 transition-transform duration-500">
                    <PlayCircle className="w-64 h-64 text-blue-600 dark:text-blue-400" />
                  </div>

                  <CardHeader className="relative z-10 pb-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-blue-600 dark:text-blue-400 mb-1">
                      <span className="px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/50">
                        {nextLesson.roadmap.name}
                      </span>
                      <span>•</span>
                      <span>Module {nextLesson.module.module_number}</span>
                    </div>
                    <CardTitle className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                      {nextLesson.lesson.title}
                    </CardTitle>
                    <CardDescription className="text-base line-clamp-2 mt-2">
                      {nextLesson.lesson.description}
                    </CardDescription>
                  </CardHeader>

                  <CardFooter className="relative z-10 pt-4">
                    <Button
                      size="lg"
                      className="gap-2 group-hover:translate-x-1 transition-transform"
                    >
                      {nextLesson.lesson.progress?.status === "in_progress"
                        ? "Continue Lesson"
                        : "Start Lesson"}
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </CardFooter>
                </Card>
              ) : (
                <Card className="h-full border-dashed border-2 flex flex-col items-center justify-center p-8 text-center bg-gray-50/50">
                  <Trophy className="w-12 h-12 text-yellow-500 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">All Caught Up!</h3>
                  <p className="text-muted-foreground mb-4">
                    You've completed all available lessons. Time to practice what
                    you've learned!
                  </p>
                  <Link to="/practice">
                    <Button>Go to the Training Hall</Button>
                  </Link>
                </Card>
              )}
            </div>

            {/* Side Cards - Quick Actions */}
            <div className="space-y-4">
              <Link to="/practice" className="block">
                <Card className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors border-l-4 border-l-orange-500">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="p-3 bg-orange-100 text-orange-600 rounded-lg dark:bg-orange-900/30">
                      <Flame className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        Training Hall
                      </h3>
                      <p className="text-sm text-gray-500">Sharpen your skills</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>

              <Link to="/analytics" className="block">
                <Card className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors border-l-4 border-l-purple-500">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="p-3 bg-purple-100 text-purple-600 rounded-lg dark:bg-purple-900/30">
                      <TrendingUp className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        Your Progress
                      </h3>
                      <p className="text-sm text-gray-500">
                        View detailed analytics
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </Link>

              <Link to="/learning-hub" className="block">
                <Card className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors border-l-4 border-l-teal-500">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="p-3 bg-teal-100 text-teal-600 rounded-lg dark:bg-teal-900/30">
                      <BookOpen className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        Learning Hub
                      </h3>
                      <p className="text-sm text-gray-500">
                        Explore all techniques
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Gamification Widget & Rank Progression */}
      {!isSchoolStudent && (
        <div className="mb-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <GamificationWidget />
          </div>
          {userXp && (
            <div>
              <RankProgression
                level={userXp.current_level}
                xp={userXp.total_xp}
                xpToNextLevel={userXp.xp_to_next_level}
              />
            </div>
          )}
        </div>
      )}

      {/* Achievements Showcase */}
      {!isSchoolStudent && (
        <div className="mb-8">
          <AchievementsShowcase />
        </div>
      )}

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Your Recent Training</CardTitle>
          <CardDescription>Your last 3 training sessions</CardDescription>
        </CardHeader>
        <CardContent>
          {stats?.recent_sessions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Target className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No training yet — your adventure starts here!</p>
              <Link to="/practice">
                <Button className="mt-4">Start Training</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {stats?.recent_sessions.slice(0, 3).map((session: any) => {
                const techniqueName = session.techniques?.name;
                const displayTitle = techniqueName
                  ? techniqueName
                  : session.mode.replace(/_/g, " ").toUpperCase();

                return (
                  <div
                    key={session.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div>
                      <p className="font-medium">{displayTitle}</p>
                      <p className="text-sm text-gray-500">
                        {session.problems_correct}/{session.problems_attempted}{" "}
                        correct
                        {techniqueName && (
                          <span className="ml-2 text-xs opacity-70">
                            ({session.mode.replace(/_/g, " ")})
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">
                        {session.problems_attempted > 0
                          ? Math.round(
                              (session.problems_correct /
                                session.problems_attempted) *
                                100,
                            )
                          : 0}
                        %
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(session.started_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
